import type { FC } from "react";
import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import DateFnsUtils from "@date-io/date-fns";
import { Box, TextField, Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";
import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { setDefaultOptions } from "date-fns";
import { enGB, nb, sv } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export interface KioDateTimePickerProps {
  onChange?: (date: string | null) => void;
  value?: string | null;
  label: string;
  description?: string;
  name?: string | undefined;
  timePickerType: "date-time" | "date" | "time";
  formValue: string | null;
  cancelLabel?: string;
  okLabel?: string;
  clearLabel?: string;
  todayLabel?: string;
  invalidDateMessage?: string;
  disabled?: boolean;
}

const DateTimePickerWrapper = styled("span")`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  .MuiTextField-root {
    width: 100%;
  }
`;

const KioDateTimePicker: FC<KioDateTimePickerProps> = ({
  timePickerType,
  name,
  description,
  formValue,
  onChange,
  label,
  cancelLabel,
  okLabel,
  clearLabel,
  todayLabel,
  invalidDateMessage,
  disabled,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { i18n } = useTranslation("common");
  if (i18n.language === "no") {
    setDefaultOptions({ locale: nb });
  } else if (i18n.language === "sv") {
    setDefaultOptions({ locale: sv });
  } else {
    setDefaultOptions({ locale: enGB });
  }

  const handleDateTimeChange = (date: any) => {
    setSelectedDate(date);
    try {
      if (timePickerType === "time") {
        if (!date || Number.isNaN(date.getTime())) {
          onChange?.(null);
          return;
        }
        // Store the picked wall-clock time as UTC "HH:mm:ss".
        // The user picks in local time; we convert to UTC before saving so the
        // backend receives a timezone-independent value.
        const hours = String(date.getUTCHours()).padStart(2, "0");
        const minutes = String(date.getUTCMinutes()).padStart(2, "0");
        const seconds = String(date.getUTCSeconds()).padStart(2, "0");
        onChange?.(`${hours}:${minutes}:${seconds}`);
      } else {
        onChange?.(date ? date?.toISOString() : null);
      }
    } catch (err) {
      if (err instanceof RangeError) {
      } else {
        throw err;
      }
    }
  };

  useEffect(() => {
    let formValueToDate: Date | null = null;
    if (formValue) {
      try {
        if (timePickerType === "time") {
          // formValue is a UTC time-of-day "HH:mm:ss" (or "HH:mm").
          // Build a Date whose UTC components match; the picker will render it
          // in the browser's local timezone.
          const parts = formValue.split(":");
          const h = Number(parts[0]);
          const m = Number(parts[1]);
          const s = Number(parts[2] ?? 0);
          if (Number.isFinite(h) && Number.isFinite(m) && Number.isFinite(s)) {
            const date = new Date();
            date.setUTCHours(h, m, s, 0);
            formValueToDate = date;
          }
        } else {
          formValueToDate = new Date(formValue);
        }
      } catch (err) {
        if (err instanceof RangeError) {
          // Bad date/time format
          formValueToDate = null;
        }
      }
      setSelectedDate(formValueToDate);
    }
  }, [formValue, timePickerType]);

  const RenderTimePicker = (timePickerType: any, commonProps: object) => {
    switch (timePickerType) {
      case "date-time":
        return (
          <DateTimePicker
            inputFormat="dd.MM.yyyy HH:mm"
            value={selectedDate}
            onChange={handleDateTimeChange}
            renderInput={(params) => <TextField variant={"filled"} {...params} />}
            {...commonProps}
          />
        );
      case "date":
        return (
          <DatePicker
            inputFormat="dd.MM.yyyy"
            value={selectedDate}
            onChange={handleDateTimeChange}
            renderInput={(params) => <TextField variant={"filled"} {...params} />}
            {...commonProps}
          />
        );
      case "time":
        return (
          <TimePicker
            inputFormat="HH:mm"
            value={selectedDate}
            onChange={handleDateTimeChange}
            renderInput={(params) => <TextField variant={"filled"} {...params} />}
            {...commonProps}
          />
        );
    }
  };

  return (
    <>
      <LocalizationProvider utils={DateFnsUtils} dateAdapter={AdapterDateFns}>
        <DateTimePickerWrapper>
          {RenderTimePicker(timePickerType, {
            clearable: true,
            fullWidth: true,
            showTodayButton: true,
            ampm: false,
            inputVariant: "filled",
            name,
            description,
            label,
            cancelLabel,
            okLabel,
            clearLabel,
            todayLabel,
            invalidDateMessage,
            disabled,
            "aria-disabled": disabled,
          })}
        </DateTimePickerWrapper>
      </LocalizationProvider>
      {!!description && (
        <Tooltip id={`${name}-description`} title={description} arrow>
          <Box p={"16px 8px 0 16px"}>
            <Info color="primary" />
          </Box>
        </Tooltip>
      )}
    </>
  );
};

export default KioDateTimePicker;
